/**
 * QuizListItem Component
 * Modern card-row hybrid layout for quiz items
 */

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiClock, FiFileText, FiUsers, FiEdit3, FiEye, FiTrash2, FiSend } from 'react-icons/fi';

const QuizCard = styled(motion.div)`
  background: #fff;
  border: 0.5px solid #d9d9d9;
  border-radius: 12px;
  padding: 14px 18px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  display: block;
  box-shadow: none;
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: 0;

  &:hover {
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    border-radius: 12px;
  }
`;

const QuizHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 8px;
  background: ${({ $colors }) => $colors.badgeBg};
  color: ${({ $colors }) => $colors.badgeText};
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const QuizContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const QuizTitle = styled.h3`
  color: var(--color-text-primary, var(--text-primary));
  font-size: 15px;
  font-weight: 500;
  margin: 0;
  line-height: 1.35;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-width: 0;
  
  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const QuizStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 520px) {
    display: flex;
    gap: 10px;
    width: 100%;
    padding: 0;
    border-bottom: none;
  }
`;

const ContentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  border-top: none;
  margin-top: 6px;
  padding-top: 0;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 640px) {
    display: flex;
    grid-template-columns: none;
    align-items: stretch;
    gap: 0.5rem;
    border-top: none;
    margin-top: 0.25rem;
    padding-top: 0;
  }
`;

const StatChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-secondary, var(--text-secondary));
  white-space: nowrap;
  
  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    color: var(--color-text-tertiary, var(--text-muted));
    opacity: 1;
  }

  @media (max-width: 520px) {
    font-size: 12px;
  }
`;

const MetaLabel = styled.span`
  color: var(--color-text-secondary, var(--text-secondary));
  font-size: 13px;
  font-weight: 400;
`;

const MetaValue = styled.span`
  color: var(--color-text-secondary, var(--text-secondary));
  font-size: 13px;
  font-weight: 400;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 15px;
  border: 0.5px solid ${({ $colors }) => $colors.buttonBg};
  border-radius: 8px;
  background: ${({ $colors }) => $colors.buttonBg};
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  width: 92px;
  min-width: 92px;
  
  &:hover:not(:disabled) {
    filter: brightness(0.95);
    box-shadow: none;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
  }

  @media (max-width: 640px) {
    width: 100%;
    min-width: 0;
    grid-column: auto;
    min-height: 34px;
  }
`;

const SecondaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 15px;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: var(--color-text-secondary, var(--text-secondary));
  font-size: 13px;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 92px;
  min-width: 92px;

  &:hover {
    border-color: #c4c4c4;
    color: var(--color-text-primary, var(--text-primary));
    background: #f9fafb;
    transform: none;
  }

  &.danger {
    margin-left: auto;
    width: 92px;
    min-width: 92px;
    border-color: #F09595;
    color: #A32D2D;
  }

  &.danger:hover {
    background: rgba(240, 149, 149, 0.08);
    border-color: #F09595;
    color: #A32D2D;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }

  @media (max-width: 640px) {
    width: 100%;
    flex: 1 1 auto;
    border-radius: 8px;
    min-height: 34px;

    &.danger {
      margin-left: 0;
      width: 100%;
      min-width: 0;
      max-width: none;
      height: auto;
      padding: 7px 14px;
    }
  }
`;

const TYPE_STYLES = {
  'final exam': { badgeBg: '#E1F5EE', badgeText: '#085041', buttonBg: '#1D9E75', label: 'Final exam' },
  homework: { badgeBg: '#EEEDFE', badgeText: '#3C3489', buttonBg: '#7F77DD', label: 'Homework' },
  midterm: { badgeBg: '#E6F1FB', badgeText: '#0C447C', buttonBg: '#185FA5', label: 'Midterm Exam' }
};

const getQuizTypeStyle = (quiz) => {
  const rawType = String(quiz.quiz_type || quiz.type || quiz.category || quiz.title || '').toLowerCase().replace(/_/g, ' ');
  if (rawType.includes('final')) return TYPE_STYLES['final exam'];
  if (rawType.includes('homework')) return TYPE_STYLES.homework;
  if (rawType.includes('midterm')) return TYPE_STYLES.midterm;
  return TYPE_STYLES.homework;
};

function QuizListItem({ 
  quiz, 
  onAssign, 
  onEdit, 
  onPreview, 
  onDelete,
  isAssigned = false,
  index = 0
}) {
  

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return datePart;
  };

  const assignmentCount = quiz.assignment_count || 0;
  const typeStyle = getQuizTypeStyle(quiz);

  return (
    <QuizCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0 }}
    >
      <QuizContent>
        <QuizHeader>
          <QuizTitle>{quiz.title}</QuizTitle>
          <TypeBadge $colors={typeStyle}>{typeStyle.label}</TypeBadge>
        </QuizHeader>
        
        <QuizStats>
          <StatChip>
            <FiFileText />
            <span>{quiz.question_count || 0}</span>
            <span className="stat-label">questions</span>
          </StatChip>
          <StatChip>
            <FiUsers />
            <span>{assignmentCount}</span>
            <span className="stat-label">assigned</span>
          </StatChip>
          <StatChip>
            <FiClock />
            <MetaLabel>Updated</MetaLabel>
            <MetaValue>{formatDateTime(quiz.updated_at || quiz.created_at)}</MetaValue>
          </StatChip>
        </QuizStats>

        <ContentActions>
          <PrimaryButton
            $colors={typeStyle}
            onClick={onAssign}
            aria-label={isAssigned ? 'View quiz' : 'Assign quiz'}
          >
            <FiSend />
            {isAssigned ? 'View' : 'Assign'}
          </PrimaryButton>
          <SecondaryActionButton onClick={onEdit} aria-label={`Edit ${quiz.title}`}>
            <FiEdit3 />
            Edit
          </SecondaryActionButton>
          <SecondaryActionButton onClick={onPreview} aria-label={`Preview ${quiz.title}`}>
            <FiEye />
            Preview
          </SecondaryActionButton>
          <SecondaryActionButton className="danger" onClick={onDelete} aria-label="Delete" title="Delete exam">
            <FiTrash2 />
            <span>Delete</span>
          </SecondaryActionButton>
        </ContentActions>
      </QuizContent>
      
    </QuizCard>
  );
}

export default QuizListItem;
